import React, { useCallback, useEffect, useState } from "react";
import { hotels, users as usersApi } from "../api/endpoints";
import SearchableSelect from "../components/SearchableSelect";
import type { ManagementCompany, User, RoleType } from "../types";

export default function UsersPage() {
  const [userList, setUserList] = useState<User[]>([]);
  const [roles, setRoles] = useState<RoleType[]>([]);
  const [managementCompanies, setManagementCompanies] = useState<ManagementCompany[]>([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [form, setForm] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    role_id: 0,
    report_management_company_id: 0,
    report_view_mode: "shop",
  });

  const loadUsers = useCallback(() => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (roleFilter) params.set("role_id", roleFilter);
    usersApi.list(params.toString()).then((res) => setUserList(res.items));
  }, [search, roleFilter]);

  useEffect(() => {
    loadUsers();
    usersApi.roles().then(setRoles);
    hotels.managementCompanies.list("limit=500").then((res) => setManagementCompanies(res.items));
  }, [loadUsers]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      ...form,
      report_management_company_id: form.report_management_company_id || null,
    };
    if (editUser) {
      await usersApi.update(editUser.id, payload);
    } else {
      await usersApi.create(payload as any);
    }
    setShowForm(false);
    setEditUser(null);
    loadUsers();
  };

  const handleEdit = (user: User) => {
    setEditUser(user);
    setForm({
      email: user.email,
      password: "",
      first_name: user.first_name,
      last_name: user.last_name,
      role_id: user.role?.id || 0,
      report_management_company_id: user.report_management_company_id || 0,
      report_view_mode: user.report_view_mode || "shop",
    });
    setShowForm(true);
  };

  const handleDeactivate = async (id: number) => {
    if (window.confirm("Deactivate this user?")) {
      await usersApi.delete(id);
      loadUsers();
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Users</h1>
        <button onClick={() => {
          setEditUser(null);
          setForm({
            email: "",
            password: "",
            first_name: "",
            last_name: "",
            role_id: 0,
            report_management_company_id: 0,
            report_view_mode: "shop",
          });
          setShowForm(true);
        }} className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
          Add User
        </button>
      </div>

      <div className="flex gap-4 mb-4">
        <input
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="border rounded px-3 py-2 flex-1"
        />
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="border rounded px-3 py-2">
          <option value="">All Roles</option>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Role</th>
              <th className="px-4 py-3 text-left">Report Access</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {userList.map((u) => (
              <tr key={u.id} className="border-t hover:bg-gray-50">
                <td className="px-4 py-3">{u.first_name} {u.last_name}</td>
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3">{u.role?.name || "-"}</td>
                <td className="px-4 py-3">
                  {u.report_management_company_name ? (
                    <div>
                      <div className="font-medium">{u.report_management_company_name}</div>
                      <div className="text-xs text-gray-500">{u.report_view_mode || "shop"}</div>
                    </div>
                  ) : (
                    "-"
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs ${u.is_active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                    {u.is_active ? "Active" : "Inactive"}
                  </span>
                  {u.is_blocked && <span className="ml-1 px-2 py-1 rounded text-xs bg-red-100 text-red-700">Blocked</span>}
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => handleEdit(u)} className="text-blue-600 hover:underline text-xs mr-2">Edit</button>
                  <button onClick={() => handleDeactivate(u.id)} className="text-red-600 hover:underline text-xs">Deactivate</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-bold mb-4">{editUser ? "Edit User" : "Add User"}</h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-3">
                <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full border rounded px-3 py-2" required />
                {!editUser && (
                  <input
                    placeholder="Password (optional)"
                    type="password"
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  />
                )}
                <input placeholder="First Name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="w-full border rounded px-3 py-2" required />
                <input placeholder="Last Name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="w-full border rounded px-3 py-2" required />
                <select value={form.role_id} onChange={(e) => setForm({ ...form, role_id: Number(e.target.value) })} className="w-full border rounded px-3 py-2">
                  <option value={0}>Select Role</option>
                  {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
                <SearchableSelect
                  options={managementCompanies.map((mc) => ({ value: mc.id, label: mc.name }))}
                  value={form.report_management_company_id}
                  onChange={(v) => setForm({ ...form, report_management_company_id: Number(v) })}
                  placeholder="No report HMC scope"
                />
                <select
                  value={form.report_view_mode}
                  onChange={(e) => setForm({ ...form, report_view_mode: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="shop">Shop reporting</option>
                  <option value="reservation">Reservation reporting</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border rounded">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
